import React, { createContext, useContext } from 'react';
import { FREE_MODE, PRICES, MODE_TEXTS } from '../config/appConfig';

/**
 * FeatureFlagContext - centralizacja logiki FREE_MODE
 * 
 * Zamiast importować FREE_MODE bezpośrednio w każdym komponencie,
 * używaj hooka useFeatureFlags() który daje dostęp do:
 * - isFreeMode: boolean - czy aplikacja jest w trybie darmowym
 * - prices: object - ceny (używane tylko w trybie płatnym)
 * - modeTexts: object - teksty dla przycisków w zależności od trybu
 * - canAccessFeature: function - sprawdza dostęp do funkcji
 */

const FeatureFlagContext = createContext(null);

export function FeatureFlagProvider({ children }) {
  const value = {
    // Główny przełącznik trybu
    isFreeMode: FREE_MODE,
    
    // Ceny (dla trybu płatnego)
    prices: PRICES,
    
    // Teksty zależne od trybu
    modeTexts: FREE_MODE ? MODE_TEXTS.free : MODE_TEXTS.paid,
    
    /**
     * Sprawdza czy użytkownik ma dostęp do funkcji
     * W trybie darmowym - zawsze true
     * W trybie płatnym - zależy od subskrypcji
     */
    canAccessFeature: (featureName, user) => {
      if (FREE_MODE) return true;
      
      // W trybie płatnym sprawdzamy subskrypcję
      switch (featureName) {
        case 'respond_to_offer':
        case 'chat':
        case 'view_contact':
          return user?.subscription?.active && 
                 user?.subscription?.expires_at && 
                 new Date(user.subscription.expires_at) > new Date();
        case 'promotion':
          // Promowanie wymaga aktywnej subskrypcji
          return user?.subscription?.active;
        default:
          return true;
      }
    },
    
    /**
     * Sprawdza czy pokazywać elementy płatności
     */
    showPaymentUI: !FREE_MODE,
    
    /**
     * Sprawdza czy funkcja wymaga subskrypcji
     */
    requiresSubscription: (featureName) => {
      if (FREE_MODE) return false;
      return ['respond_to_offer', 'chat', 'view_contact'].includes(featureName);
    },
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlags() {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
}

// Export dla kompatybilności wstecznej - komponenty mogą nadal importować FREE_MODE
export { FREE_MODE };
